import type { StylePropertyPage } from '@/api/my-styles/types';
import type {
  CompanySearchPage,
  ConnectionType,
  CreateCompanyPayload,
  CreateCompanyResponse,
  DataPointSearchResult,
  EsgTemplateMetadata,
  PatchAnswerPayload,
  PatchTemplatePayload,
  ProfileTemplate,
} from '@/api/network/types';
import type { MyNetworkResponse } from '@/api/questionnaires/types';
import {
  API_ROOT_URL,
  BACKEND_URL,
  CASCADE_TRACING_API_URL,
} from '@/lib/constants/api';
import { apiFetch } from '@/lib/utils/apiFetch';
import type {
  ExportNetworkPayload,
  LinkedStylesPage,
  NetworkExportCatalogue,
} from './types';

export async function fetchMyNetwork(qs: string): Promise<MyNetworkResponse> {
  return apiFetch<MyNetworkResponse>(`/api/companies?${qs}`, {}, API_ROOT_URL);
}

export async function fetchNetworkMapCompanies(
  qs: string,
): Promise<MyNetworkResponse> {
  return apiFetch<MyNetworkResponse>(`/api/companies?${qs}`, {}, API_ROOT_URL);
}

// ─── Inline edit ─────────────────────────────────────────────────────────────

export async function patchNetworkCompanyAnswer(
  payload: PatchAnswerPayload,
): Promise<void> {
  await apiFetch<void>(
    '/companies/templates/about/answer',
    { method: 'PATCH', body: JSON.stringify(payload) },
    CASCADE_TRACING_API_URL,
  );
}

// ─── Bulk actions ─────────────────────────────────────────────────────────────

// Lives on the main API gateway (`/api/companies/bulk-unlink`), NOT on the
// companies-service microservice — mirrors `unlinkCompany` above.
export async function bulkUnlinkCompanies(
  forCompanyIds: string[],
): Promise<void> {
  await apiFetch<void>(
    '/api/companies/bulk-unlink',
    { method: 'PATCH', body: JSON.stringify({ forCompanyIds }) },
    API_ROOT_URL,
  );
}

export async function exportNetworkCompanies(
  payload: ExportNetworkPayload,
): Promise<void> {
  await apiFetch<void>(
    '/companies/export',
    { method: 'POST', body: JSON.stringify(payload) },
    BACKEND_URL,
  );
}

export async function fetchNetworkExportCatalogue(
  ids: string[],
  includeOwnCompany: boolean,
): Promise<NetworkExportCatalogue> {
  return apiFetch<NetworkExportCatalogue>(
    '/companies/export/options',
    { method: 'POST', body: JSON.stringify({ ids, includeOwnCompany }) },
    BACKEND_URL,
  );
}

export async function recalculateAssessments(
  companyIds: string[],
): Promise<void> {
  await apiFetch<void>(
    '/api/risk-analysis/assessments/calculate/standard-assessment',
    { method: 'POST', body: JSON.stringify({ companyIds }) },
    API_ROOT_URL,
  );
}

// ─── Vector search (add company) ─────────────────────────────────────────────

export async function searchCompanies(qs: string): Promise<CompanySearchPage> {
  return apiFetch<CompanySearchPage>(
    `/api/companies/vector-search?${qs}`,
    {},
    API_ROOT_URL,
  );
}

// Lives on the main API gateway (`/api/companies/link`), NOT on the
// companies-service microservice — mirrors `searchEsgDataPoints` below.
export async function linkCompany(payload: {
  forCompanyId: string;
  connectionType: ConnectionType;
}): Promise<void> {
  await apiFetch<void>(
    '/api/companies/link',
    { method: 'POST', body: JSON.stringify(payload) },
    API_ROOT_URL,
  );
}

// Create a brand-new company. Passing connectionType also links it to the
// caller's network (backend CompanyCreationService → linkOtherId).
export async function createCompany(
  payload: CreateCompanyPayload,
): Promise<CreateCompanyResponse> {
  return apiFetch<CreateCompanyResponse>(
    '/api/companies',
    { method: 'POST', body: JSON.stringify(payload) },
    API_ROOT_URL,
  );
}

export async function unlinkCompany(forCompanyId: string): Promise<void> {
  await apiFetch<void>(
    '/api/companies/unlink',
    { method: 'POST', body: JSON.stringify({ forCompanyId }) },
    API_ROOT_URL,
  );
}

// ─── Linked styles / collections (from network/-api) ──────────────────────────

export async function fetchLinkedStyles(body: {
  linkedCompanyId: string;
  page: number;
  limit: number;
  include: string[];
  isArchived: boolean;
  sort?: string;
  order?: string;
  name?: string;
}): Promise<LinkedStylesPage> {
  return apiFetch<LinkedStylesPage>(
    '/styles/list',
    { method: 'POST', body: JSON.stringify(body) },
    BACKEND_URL,
  );
}

// Also defined in my-styles/-api/index.ts — consolidate to src/api/ if more route families need it.
export async function fetchCollectionProperties(body: {
  category: 'collections';
  companyId: string;
  limit?: number;
}): Promise<StylePropertyPage> {
  return apiFetch<StylePropertyPage>(
    '/style-properties/list',
    { method: 'POST', body: JSON.stringify(body) },
    BACKEND_URL,
  );
}

// ─── ESG templates / profiles (from esg.ts) ───────────────────────────────────

export async function fetchEsgTemplates(): Promise<{
  data: EsgTemplateMetadata[];
}> {
  return apiFetch<{ data: EsgTemplateMetadata[] }>(
    '/api/companies/templates?page=0&count=500&filter=%7B%7D',
    {},
    API_ROOT_URL,
  );
}

// Lives on the main API gateway (`/api/companies/impact-areas/hierarchy/:sectionId`),
// NOT on the companies-service microservice — mirrors `patchProfileTemplate`.
export async function fetchImpactAreaHierarchy(): Promise<
  Array<{
    id: string;
    name: string;
    parentId?: string;
    sectionId?: string;
    isParent?: number;
  }>
> {
  const res = await apiFetch<
    Array<{
      id: string;
      name: string;
      parentId?: string;
      sectionId?: string;
      isParent?: number;
    }>
  >('/api/companies/impact-areas/hierarchy/2', {}, API_ROOT_URL);
  return Array.isArray(res) ? res : [];
}

export async function fetchEsgProfileTemplate(
  templateId: string,
  companyId: string,
): Promise<ProfileTemplate> {
  // Lives on the main API gateway (`/api/companies/templates/:templateId/profile/:forCompanyId`),
  // NOT on the companies-service microservice — mirrors `patchEsgTemplate`.
  const res = await apiFetch<ProfileTemplate>(
    `/api/companies/templates/${templateId.toLowerCase()}/profile/${companyId}`,
    {},
    API_ROOT_URL,
  );
  if (!res) throw new Error('ESG profile template not found');
  return res;
}

export async function patchEsgTemplate(
  templateId: string,
  payload: PatchTemplatePayload,
): Promise<void> {
  await apiFetch<void>(
    `/api/companies/templates/${templateId.toLowerCase()}/answer`,
    { method: 'PATCH', body: JSON.stringify(payload) },
    API_ROOT_URL,
  );
}

// Lives on the main API gateway (`/api/companies/:forCompanyId/data-points-answers`),
// NOT on the companies-service microservice — mirrors `patchEsgTemplate`.
export async function searchEsgDataPoints(
  companyId: string,
  qs: string,
): Promise<{ data: DataPointSearchResult[] }> {
  return apiFetch<{ data: DataPointSearchResult[] }>(
    `/api/companies/${companyId}/data-points-answers?${qs}`,
    {},
    API_ROOT_URL,
  );
}
